import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { 
  FileText, 
  DollarSign, 
  Save, 
  Eye, 
  Clock, 
  Calendar,
  MessageCircle,
  Image,
  Layout,
  Loader2,
  Archive,
  Brain,
  Zap,
  Star,
  Heart,
  Target,
  Sparkles,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/lib/supabase';

interface ContentSection {
  id: string;
  title: string;
  content: string;
  page: string;
}

interface ServicePrice {
  id: string;
  name: string;
  price: number;
  duration: string;
  description: string;
  features?: string[];
}

interface ArchiveDocument {
  id: string;
  title: string;
  content: string;
  category: string;
  date: string;
  author: string;
}

export function AdminContentManager() {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // État pour les contenus des pages
  const [contentSections, setContentSections] = useState<ContentSection[]>([]);

  // État pour les tarifs
  const [services, setServices] = useState<ServicePrice[]>([]);

  // État pour les archives
  const [archives, setArchives] = useState<ArchiveDocument[]>([]);

  // Charger les données au montage du composant
  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        
        // Charger les sections de contenu
        const { data: contentData, error: contentError } = await supabase
          .from('admin_settings')
          .select('*')
          .eq('key', 'content_sections')
          .single();

        if (contentError && contentError.code !== 'PGRST116') {
          throw contentError;
        }

        if (contentData?.value) {
          setContentSections(contentData.value);
        }

        // Charger les services
        const { data: servicesData, error: servicesError } = await supabase
          .from('admin_settings')
          .select('*')
          .eq('key', 'services')
          .single();

        if (servicesError && servicesError.code !== 'PGRST116') {
          throw servicesError;
        }

        if (servicesData?.value) {
          setServices(servicesData.value);
        }

        // Charger les archives
        const { data: archivesData, error: archivesError } = await supabase
          .from('admin_settings')
          .select('*')
          .eq('key', 'archives')
          .single();

        if (archivesError && archivesError.code !== 'PGRST116') {
          throw archivesError;
        }

        if (!archivesData?.value) {
          // Créer l'archive du Baromètre Énergétique
          const barometreArchive: ArchiveDocument = {
            id: 'barometre-energetique-doc',
            title: 'Documentation du Baromètre Énergétique',
            category: 'Documentation Technique',
            date: new Date().toISOString(),
            author: 'Admin',
            content: `# Le Baromètre Énergétique HED

## 🌟 Qu'est-ce que le Baromètre Énergétique ?

Le Baromètre Énergétique est une interface intuitive et personnalisée qui permet à un utilisateur de :

- Mesurer son niveau d'énergie globale à un instant T
- Identifier les centres énergétiques affectés ou équilibrés
- Recevoir des recommandations et des réponses personnalisées
- Suivre son évolution énergétique dans le temps

Il s'appuie sur le Human Design, mais va au-delà en intégrant des principes d'intelligence émotionnelle, de body mapping, et d'alignement énergétique holistique.

## 🧩 Composantes fonctionnelles

### 1. Niveau d'énergie global (%)
- Représenté par une barre horizontale colorée (du rouge au vert)
- Calibré en fonction du nombre de symptômes "non alignés" ou "positifs" sélectionnés
- Permet une lecture instantanée de l'état énergétique

### 2. Diagnostic en temps réel
- Basé sur une question ouverte : "Comment te sens-tu aujourd'hui ?"
- L'utilisateur coche des symptômes physiques, émotionnels ou psychiques selon son ressenti
- Ces symptômes sont mappés par type (Projector, Generator, etc.) et liés à des centres énergétiques

## 🧠 Centres Énergétiques analysés

Chaque symptôme sélectionné alimente un ou plusieurs centres parmi :

- Ajna (Intuition, vision, clarté mentale)
- Cœur (Will Center) (Estime de soi, volonté, reconnaissance)
- Sacral (Énergie vitale, endurance, plaisir)
- Gorge (Expression, communication)
- Égo (Volonté personnelle)
- Plexus Solaire (Émotions, sentiments, cycles émotionnels)
- Rate (Instinct, survie, immunité)
- Tête (Inspiration, pressions mentales)
- Racine (Stress, impulsion à l'action)
- Centre G (Amour, direction, identité)

Chaque centre peut être :
- Aligné (aucun symptôme associé)
- Affecté légèrement (1 symptôme léger)
- Affecté intensément (2 symptômes ou plus)

## 🛠️ Fonctionnalités clés

### ✅ Sélection des symptômes
- Interface sous forme de boutons à cocher
- 10 propositions par type + option "Autre" avec zone de texte libre
- Peut être enrichi dynamiquement avec une liste exhaustive de ressentis

### 🧘 Réponse personnalisée
- Générée automatiquement selon les symptômes
- Exemples :
  - "Repose-toi et réfléchis à ce qui te pèse émotionnellement."
  - "Continue à suivre ton intuition et respecte ton rythme naturel."
- Possibilité future : proposer une action concrète (respiration, méditation, mantra, posture...)

### 📉 Historique des diagnostics
- Enregistrement des diagnostics passés avec date, score, type, et centres touchés
- Courbe d'évolution énergétique dans le temps (éventuellement graphique)

### 🪞 Affichage des centres énergétiques
- Carte claire des centres actifs, affectés ou équilibrés
- Codes couleurs (vert = aligné, orange = sensible, rouge = désaligné)
- Affichage des symptômes spécifiques reliés à chaque centre

## 🧠 Système logique et calcul

- Chaque symptôme est associé à un ou plusieurs centres + une couleur de zone (rouge, orange, verte)
- L'état énergétique est évalué sur la densité des symptômes sélectionnés :
  - Score haut : beaucoup de réponses positives ➜ niveau vert
  - Score moyen : quelques symptômes de désalignement ➜ niveau orange
  - Score faible : symptômes lourds ou nombreux ➜ niveau rouge

## 📲 Interactions avec d'autres composants

- Accueil / Dashboard : Résumé du dernier diagnostic, état énergétique et bouton Nouveau Diagnostic
- Profil utilisateur : Affiche le type Human Design, nombre de diagnostics effectués, progression
- Historique : Vue chronologique, filtres, export possible
- Admin Dashboard : Vue d'ensemble anonymisée (utile en coaching ou accompagnement de groupe)

## 🧬 Évolutions envisagées

### ✨ Intelligence symptomatique étendue
- Ajouter une IA capable d'analyser les notes personnelles pour détecter :
  - Symptômes non listés
  - Émotions sous-jacentes
  - Besoins énergétiques implicites

### 🌀 Mode "Entraînement à l'énergie"
- Proposer à l'utilisateur des exercices ou challenges pour "activer" volontairement des centres :
  - Respiration pour le Plexus
  - Voix et chant pour la Gorge
  - Écriture intuitive pour Ajna

### 🛎️ Notifications / Check-in
- Demander à l'utilisateur "Comment te sens-tu aujourd'hui ?" chaque matin
- L'aider à réguler son énergie quotidiennement

### 🤝 Système de partage
- Permettre de partager un état énergétique avec un coach, un thérapeute, ou un cercle de soutien
- Création de profils multiples (famille, collaborateurs, etc.)

### 🛡️ Sécurité & données
- Toutes les données sont stockées de manière sécurisée
- L'utilisateur peut télécharger, supprimer ou exporter ses diagnostics
- Conforme RGPD

## 🪄 En résumé

Le Baromètre Énergétique est :
- Un miroir intérieur énergétique
- Un outil de prise de conscience rapide
- Une boussole vers l'alignement
- Un compagnon de croissance personnelle`
          };

          const { error: insertError } = await supabase
            .from('admin_settings')
            .upsert({
              key: 'archives',
              value: [barometreArchive],
              category: 'documentation',
              description: 'Archives et documentation du système'
            });

          if (insertError) throw insertError;
          setArchives([barometreArchive]);
        } else {
          setArchives(archivesData.value);
        }

      } catch (error) {
        console.error('Error loading content:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les contenus.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Sauvegarder les sections de contenu
      const { error: contentError } = await supabase
        .from('admin_settings')
        .upsert({
          key: 'content_sections',
          value: contentSections,
          category: 'content',
          description: 'Sections de contenu des pages'
        }, {
          onConflict: 'key'
        });

      if (contentError) throw contentError;

      // Sauvegarder les services
      const { error: servicesError } = await supabase
        .from('admin_settings')
        .upsert({
          key: 'services',
          value: services,
          category: 'content',
          description: 'Services et tarifs'
        }, {
          onConflict: 'key'
        });

      if (servicesError) throw servicesError;

      // Sauvegarder les archives
      const { error: archivesError } = await supabase
        .from('admin_settings')
        .upsert({
          key: 'archives',
          value: archives,
          category: 'documentation',
          description: 'Archives et documentation du système'
        }, {
          onConflict: 'key'
        });

      if (archivesError) throw archivesError;
      
      toast({
        title: "Modifications enregistrées",
        description: "Les contenus ont été mis à jour avec succès.",
      });

    } catch (error) {
      console.error('Error saving content:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de sauvegarder les modifications.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-600" />
          <h2 className="text-lg font-semibold">Gestion des contenus</h2>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="content">
          <TabsList className="grid grid-cols-4 gap-4 mb-6">
            <TabsTrigger value="content" className="flex items-center gap-2">
              <Layout className="w-4 h-4" />
              Pages
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Services & Tarifs
            </TabsTrigger>
            <TabsTrigger value="archives" className="flex items-center gap-2">
              <Archive className="w-4 h-4" />
              Archives
            </TabsTrigger>
            <TabsTrigger value="media" className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              Médias
            </TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="space-y-6">
            {/* Sections de contenu par page */}
            <div className="space-y-6">
              {contentSections.map((section) => (
                <Card key={section.id}>
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium">{section.title}</h3>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Eye className="w-4 h-4" />
                          Aperçu
                        </Button>
                      </div>
                      <textarea
                        value={section.content}
                        onChange={(e) => setContentSections(prev =>
                          prev.map(s => s.id === section.id ? { ...s, content: e.target.value } : s)
                        )}
                        className="w-full min-h-[100px] p-3 border rounded-md"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="services" className="space-y-6">
            {/* Services et tarifs */}
            {services.map((service) => (
              <Card key={service.id}>
                <CardContent className="p-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nom du service
                        </label>
                        <input
                          type="text"
                          value={service.name}
                          onChange={(e) => setServices(prev =>
                            prev.map(s => s.id === service.id ? { ...s, name: e.target.value } : s)
                          )}
                          className="w-full p-2 border rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prix (€)
                        </label>
                        <input
                          type="number"
                          value={service.price}
                          onChange={(e) => setServices(prev =>
                            prev.map(s => s.id === service.id ? { ...s, price: parseInt(e.target.value) } : s)
                          )}
                          className="w-full p-2 border rounded-md"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Durée
                        </label>
                        <input
                          type="text"
                          value={service.duration}
                          onChange={(e) => setServices(prev =>
                            prev.map(s => s.id === service.id ? { ...s, duration: e.target.value } : s)
                          )}
                          className="w-full p-2 border rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={service.description}
                          onChange={(e) => setServices(prev =>
                            prev.map(s => s.id === service.id ? { ...s, description: e.target.value } : s)
                          )}
                          className="w-full p-2 border rounded-md"
                        />
                      </div>
                    </div>

                    {/* Features List */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Caractéristiques incluses
                      </label>
                      {service.features?.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 mb-2">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => {
                              const newFeatures = [...(service.features || [])];
                              newFeatures[index] = e.target.value;
                              setServices(prev =>
                                prev.map(s => s.id === service.id ? { ...s, features: newFeatures } : s)
                              );
                            }}
                            className="w-full p-2 border rounded-md"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newFeatures = service.features?.filter((_, i) => i !== index);
                              setServices(prev =>
                                prev.map(s => s.id === service.id ? { ...s, features: newFeatures } : s)
                              );
                            }}
                          >
                            <Image className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newFeatures = [...(service.features || []), 'Nouvelle caractéristique'];
                          setServices(prev =>
                            prev.map(s => s.id === service.id ? { ...s, features: newFeatures } : s)
                          );
                        }}
                      >
                        Ajouter une caractéristique
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button
              variant="outline"
              onClick={() => setServices(prev => [...prev, {
                id: `service-${Date.now()}`,
                name: 'Nouveau service',
                price: 0,
                duration: '60 minutes',
                description: 'Description du service',
                features: ['Caractéristique 1', 'Caractéristique 2', 'Caractéristique 3']
              }])}
              className="w-full"
            >
              Ajouter un service
            </Button>
          </TabsContent>

          <TabsContent value="archives" className="space-y-6">
            {/* Archives et Documentation */}
            <div className="space-y-6">
              {archives.map((archive) => (
                <Card key={archive.id} className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4 mb-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-purple-100 rounded-lg">
                          <Brain className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold mb-1">{archive.title}</h3>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(archive.date).toLocaleDateString('fr-FR')}
                            </span>
                            <span className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              {archive.category}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="shrink-0">
                        <Eye className="w-4 h-4 mr-2" />
                        Aperçu
                      </Button>
                    </div>

                    <div className="prose max-w-none">
                      <textarea
                        value={archive.content}
                        onChange={(e) => setArchives(prev =>
                          prev.map(a => a.id === archive.id ? { ...a, content: e.target.value } : a)
                        )}
                        className="w-full min-h-[500px] p-4 border rounded-lg font-mono text-sm"
                      />
                    </div>

                    <div className="flex items-center gap-4 mt-6 pt-6 border-t">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span>Dernière modification : {new Date(archive.date).toLocaleString('fr-FR')}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <MessageCircle className="w-4 h-4" />
                        <span>Auteur : {archive.author}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="media" className="space-y-6">
            {/* Gestionnaire de médias */}
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Image className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600">
                Glissez-déposez des images ici ou cliquez pour sélectionner des fichiers
              </p>
              <input type="file" className="hidden" accept="image/*" multiple />
            </div>
          </TabsContent>
        </Tabs>

        {/* Bouton de sauvegarde */}
        <div className="flex justify-end mt-6">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sauvegarde en cours...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Sauvegarder les modifications
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}